'use client'

import { useMemo, useState } from 'react'

import { CodeSnippet } from '@graftcode/design-system'

import { MULTI_CODE_SNIPPET_TEST_IDS } from '@common/common-test-ids.const'
import { mapRuntimeToSnippetLanguage } from '@common/components/MultiCodeSnippet/mapRuntimeToSnippetLanguage'
import { formatQuickStartLanguageLabel } from '@common/utils/formatQuickStartLanguageLabel'

export type MultiCodeVariant = {
  runtime: string
  language: string
  code: string
}

type MultiCodeSnippetProps = {
  variants: MultiCodeVariant[]
  className?: string
}

export function MultiCodeSnippet({ variants, className }: MultiCodeSnippetProps) {
  const [activeIndex, setActiveIndex] = useState(0)

  const active = variants[activeIndex] ?? variants[0]
  const highlightLanguage = mapRuntimeToSnippetLanguage(active?.language || active?.runtime || 'text')

  const tabs = useMemo(
    () =>
      variants.map((variant, index) => ({
        label: formatQuickStartLanguageLabel(variant.runtime),
        active: index === activeIndex,
      })),
    [variants, activeIndex]
  )

  if (!active) return null

  return (
    <CodeSnippet
      data-testid={MULTI_CODE_SNIPPET_TEST_IDS.root}
      className={className}
      code={active.code}
      language={highlightLanguage}
      contentVariant="advanced"
      tabs={tabs}
      onTabChange={(label) => {
        const nextIndex = variants.findIndex((variant) => formatQuickStartLanguageLabel(variant.runtime) === label)
        if (nextIndex >= 0) setActiveIndex(nextIndex)
      }}
    />
  )
}
